import { useState, useEffect, useMemo } from "react";
import {
  TextField,
  Typography,
  Stack,
  Button,
  Avatar,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import imageFileToBase64 from "../../../../../hooks/imageToBase64";
import { useToast } from "../../../../shared/toastMessages/toastProvider";

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const UserDetails = ({ userInfo, editProfile, theme }) => {
  const [formData, setFormData] = useState(null);
  const [userIcon, setUserIcon] = useState("");
  const [initialData, setInitialData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (userInfo) {
      setFormData(userInfo);
      setUserIcon(userInfo.icon || "");
      setInitialData({
        name: userInfo.name || "",
        icon: userInfo.icon || "",
      });
    }
  }, [userInfo]);

  const hasChanges = useMemo(() => {
    if (!initialData || !formData) return false;
    return (
      (formData.name || "") !== initialData.name ||
      userIcon !== initialData.icon
    );
  }, [formData, userIcon, initialData]);

  if (!formData) {
    return (
      <Box
        sx={{
          py: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress color="primary" size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading profile details...
        </Typography>
      </Box>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const base64 = await imageFileToBase64(file, 200);
      setUserIcon(base64);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Unable to process image.", "error");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async () => {
    if (!hasChanges || saving) return;

    setSaving(true);
    try {
      await editProfile({
        ...formData,
        name: (formData.name || "").trim(),
        icon: userIcon,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <PersonOutlinedIcon fontSize="small" color="action" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Profile details
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Update your display name and profile avatar.
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || saving || !(formData.name || "").trim()}
          theme={theme}
          startIcon={<SaveOutlinedIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 2.5,
            alignSelf: { xs: "stretch", sm: "center" },
          }}
        >
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems={{ xs: "stretch", md: "flex-start" }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 220 },
            flexShrink: 0,
            p: 2,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "#fafafa",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Avatar
            src={userIcon}
            alt={formData.name || "Profile"}
            sx={{
              width: 96,
              height: 96,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
              color: "text.secondary",
              fontSize: 36,
              fontWeight: 600,
            }}
          >
            {(formData.name || "U").charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: "center" }}>
            Profile avatar
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", mb: 0.5 }}
          >
            Optional image shown on your account.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            size="small"
            startIcon={<PhotoCameraOutlinedIcon />}
            disabled={uploadingAvatar}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
            }}
            theme={theme}
          >
            {uploadingAvatar ? "Processing..." : "Upload avatar"}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
          </Button>
        </Box>

        <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Username
            </Typography>
            <TextField
              name="name"
              value={formData.name || ""}
              onChange={handleChange}
              fullWidth
              size="small"
              placeholder="Enter a display name"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Email
            </Typography>
            <TextField
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              disabled
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Account role
            </Typography>
            <TextField
              name="job_role"
              value={formData.job_role || ""}
              onChange={handleChange}
              disabled
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Time created
            </Typography>
            <TextField
              value={formatTime(formData.time_created)}
              disabled
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary">
            {hasChanges ? "You have unsaved changes." : "No changes to save."}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default UserDetails;
